import React, { useEffect } from 'react';

interface KeyboardShortcut {
  keys: string[];
  description: string;
}

interface ShortcutCategory {
  category: string;
  shortcuts: KeyboardShortcut[];
}

interface KeyboardShortcutsHelpProps {
  isOpen: boolean;
  onClose: () => void;
}

const KeyboardShortcutsHelp: React.FC<KeyboardShortcutsHelpProps> = ({ isOpen, onClose }) => {
  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const shortcutCategories: ShortcutCategory[] = [
    {
      category: 'General',
      shortcuts: [
        { keys: ['⌘', 'K'], description: 'Open command palette' },
        { keys: ['?'], description: 'Show keyboard shortcuts' },
        { keys: ['Esc'], description: 'Close modal or dialog' },
      ],
    },
    {
      category: 'Navigation',
      shortcuts: [
        { keys: ['G', 'I'], description: 'Go to Inbox' },
        { keys: ['G', 'T'], description: 'Go to Tasks' },
        { keys: ['G', 'C'], description: 'Go to Calendar' },
        { keys: ['G', 'P'], description: 'Go to Contacts' },
        { keys: ['G', 'G'], description: 'Go to Goals' },
        { keys: ['G', 'N'], description: 'Go to Notes' },
      ],
    },
    {
      category: 'Actions',
      shortcuts: [
        { keys: ['C'], description: 'Compose new email' },
        { keys: ['N'], description: 'Create new task' },
        { keys: ['R'], description: 'Reply to email' },
        { keys: ['A'], description: 'Archive email' },
        { keys: ['/'], description: 'Focus search' },
      ],
    },
    {
      category: 'Selection',
      shortcuts: [
        { keys: ['J'], description: 'Next email/task' },
        { keys: ['K'], description: 'Previous email/task' },
        { keys: ['↑', '↓'], description: 'Navigate items' },
        { keys: ['Enter'], description: 'Open selected item' },
        { keys: ['X'], description: 'Toggle selection' },
        { keys: ['Shift', 'Click'], description: 'Range select' },
        { keys: ['⌘', 'A'], description: 'Select all' },
      ],
    },
    {
      category: 'Email',
      shortcuts: [
        { keys: ['E'], description: 'Archive email' },
        { keys: ['U'], description: 'Mark as unread' },
        { keys: ['S'], description: 'Star/unstar email' },
        { keys: ['!'], description: 'Mark as spam' },
      ],
    },
    {
      category: 'Tasks',
      shortcuts: [
        { keys: ['Space'], description: 'Toggle task complete' },
        { keys: ['D'], description: 'Set due date' },
        { keys: ['P'], description: 'Set priority' },
        { keys: ['Delete'], description: 'Delete task' },
      ],
    },
  ];

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-semibold text-gray-900">Keyboard Shortcuts</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition"
            aria-label="Close"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {shortcutCategories.map((category) => (
              <div key={category.category}>
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                  {category.category}
                </h3>
                <div className="space-y-2">
                  {category.shortcuts.map((shortcut, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between py-2 px-3 rounded-md hover:bg-gray-50 transition"
                    >
                      <span className="text-sm text-gray-700">{shortcut.description}</span>
                      <div className="flex items-center gap-1">
                        {shortcut.keys.map((key, keyIndex) => (
                          <React.Fragment key={keyIndex}>
                            {keyIndex > 0 && (
                              <span className="text-gray-400 text-xs mx-1">then</span>
                            )}
                            <kbd className="px-2 py-1 text-xs font-semibold text-gray-700 bg-gray-100 border border-gray-300 rounded min-w-[28px] text-center">
                              {key}
                            </kbd>
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default KeyboardShortcutsHelp;

